<template>
  <v-app id="keep" class="theme-shell theme-shell--student">
    <ThemeDecorations />
    <v-layout wrap>
      <v-app-bar
        id="header_style"
        v-if="inited && !login"
        app
        clipped-left
        class="theme-header"
      >
        <div class="student-toolbar">
          <div class="student-toolbar__side student-toolbar__side--left">
            <v-app-bar-nav-icon
              class="hamburger_icon"
              @click="drawer = !drawer"
            />
            <div
              class="pa-2 white--text cursor-pointer"
              v-if="showLeftToolbarItems"
              @click="toCalendar"
            >
              Календар
            </div>
            <div
              class="pa-2 white--text cursor-pointer"
              v-if="showLeftToolbarItems"
              @click="toLibrary"
            >
              Бібліотека
            </div>
          </div>

          <button
            type="button"
            class="student-toolbar__brand"
            :class="{ 'student-toolbar__brand--doodle': hasHolidayDoodle }"
            aria-label="Перейти на головну сторінку"
            title="На головну"
            @click="toMain"
          >
            <span class="student-toolbar__logo-wrap">
              <HolidayDoodle />
              <span class="student-toolbar__logo" aria-hidden="true"></span>
            </span>
            <div class="title student-toolbar__brand-name">
              RedFox Homeschool&nbsp;
            </div>
          </button>

          <div class="student-toolbar__side student-toolbar__side--right">
            <div
              v-if="!isGuest"
              class="student-toolbar__user white--text"
              :title="studentLabel"
            >
              {{ user.firstName }} {{ user.lastName }}
              <span v-if="user.grade">{{ user.grade }}-й клас</span>
            </div>
            <div v-else class="student-toolbar__user white--text">
              Демо
            </div>
            <v-menu
              v-if="!isGuest"
              v-model="notificationMenuOpen"
              offset-y
              left
              :close-on-content-click="false"
              content-class="student-notifications-menu"
            >
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="student-toolbar__notification"
                  :aria-label="notificationButtonLabel"
                  :title="notificationButtonLabel"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon color="white">mdi-bell-outline</v-icon>
                  <span
                    v-if="unreadNotifications.length"
                    class="student-toolbar__notification-dot"
                    aria-hidden="true"
                  ></span>
                </button>
              </template>

              <section class="student-notifications" aria-label="Нові повідомлення">
                <header class="student-notifications__header">
                  <div>
                    <strong>Повідомлення</strong>
                    <span v-if="unreadNotifications.length">
                      {{ unreadNotifications.length }} непрочитаних
                    </span>
                  </div>
                  <v-icon size="20">mdi-message-text-outline</v-icon>
                </header>
                <div
                  v-if="unreadNotifications.length"
                  class="student-notifications__list"
                >
                  <button
                    v-for="notification in unreadNotifications"
                    :key="notification.id"
                    type="button"
                    @click="openNotification(notification)"
                  >
                    <span class="student-notifications__unread" aria-hidden="true"></span>
                    <span class="student-notifications__content">
                      <strong>{{ notification.lesson_name }}</strong>
                      <span>{{ notificationPreview(notification) }}</span>
                      <small>
                        {{ notification.course_name }} ·
                        {{ formatNotificationDate(notification.sendtime) }}
                      </small>
                    </span>
                    <v-icon size="19">mdi-chevron-right</v-icon>
                  </button>
                </div>
                <div v-else class="student-notifications__empty">
                  <v-icon size="27">mdi-bell-check-outline</v-icon>
                  <span>Нових повідомлень немає</span>
                </div>
              </section>
            </v-menu>
            <button
              class="student-toolbar__logout"
              type="button"
              aria-label="Налаштування оформлення"
              title="Налаштування оформлення"
              @click="toSettings"
            >
              <v-icon color="white">mdi-cog-outline</v-icon>
            </button>
            <button
              class="student-toolbar__logout"
              type="button"
              aria-label="Вийти"
              @click="logout"
            >
              <v-icon color="white" v-html="'$vuetify.icons.logout'"></v-icon>
            </button>
          </div>
        </div>
      </v-app-bar>

      <v-navigation-drawer
        v-if="inited && !login"
        v-model="drawer"
        app
        clipped
        class="student-navigation-drawer theme-sidebar"
        :mini-variant="miniVariant && showLeftToolbarItems"
        mini-variant-width="72"
        width="300"
      >
        <v-list
          class="student-navigation-list theme-sidebar-surface mobile-paddings"
        >
          <CourseItem
            :compact="miniVariant && showLeftToolbarItems"
            :small-screen="!showLeftToolbarItems"
            @courseItemsEvent="courseItemsEventHandler"
          />
        </v-list>
      </v-navigation-drawer>

      <button
        v-if="
          inited && !login && showLeftToolbarItems && showCourseNavigation
        "
        type="button"
        class="student-drawer-toggle"
        :class="{
          'student-drawer-toggle--open': drawer && !miniVariant,
          'student-drawer-toggle--compact': drawer && miniVariant,
        }"
        :aria-label="sidebarToggleLabel"
        :title="sidebarToggleLabel"
        @click="toggleSidebarSize"
      >
        <v-icon class="theme-navigation-accent" size="22">
          {{
            drawer && !miniVariant ? "mdi-chevron-left" : "mdi-chevron-right"
          }}
        </v-icon>
      </button>

      <component
        :is="currentPopup"
        :popupContext="currentPopupContext"
        @popupEvent="popupEventHandler"
      ></component>
      <router-view v-if="inited" />
    </v-layout>
  </v-app>
</template>

<script>
import RestService from "@/services/RestService";
import GoogleAnaliticService from "@/services/GoogleAnaliticService";
import CourseItem from "@/components/Pages/ClassPage/CourseItem";
import ThemeDecorations from "@/components/Base/ThemeDecorations/ThemeDecorations";
import HolidayDoodle from "@/components/Base/HolidayDoodle/HolidayDoodle";
import appName from "@/enums/appName";
import statesEnum from "@/enums/statesEnum";
import popupEnum from "@/enums/popupEnum";
import PopupEventEnum from "@/enums/PopupEventEnum";
import MessageService from "@/services/MessageService";

import appMixin from "@/components/Mixin/appMixin";

export default {
  name: "App",
  components: { CourseItem, HolidayDoodle, ThemeDecorations },
  mixins: [appMixin],
  data() {
    return {
      currentPopup: null,
      currentPopupContext: {},
      inited: false,
      urlParams: {},
      drawer: true,
      color: "primary",
      colors: ["primary", "blue", "success", "red", "teal"],
      right: false,
      permanent: true,
      miniVariant: false,
      expandOnHover: false,
      background: false,
      headers: [
        {
          text: "Lessons",
          align: "start",
          sortable: false,
          value: "name",
          filter: false,
        },
      ],
      notificationMenuOpen: false,
      unreadNotifications: [],
      notificationRefreshTimer: null,
    };
  },
  computed: {
    hasHolidayDoodle() {
      const doodle = this.$store.getters["ThemeStore/getConfig"]?.toolbarDoodle;
      return Boolean(doodle && doodle.enabled !== false && doodle.variant);
    },
    showCourseNavigation() {
      return this.stateName !== statesEnum.SETTINGS;
    },
    showLeftToolbarItems() {
      return (
        this.isStudentApp &&
        !this.$vuetify.breakpoint.xsOnly &&
        !this.$vuetify.breakpoint.smOnly
      );
    },
    isStudentApp() {
      return this.$store.getters["ConfigStore/isStudentApp"];
    },
    isTeacherApp() {
      return this.$store.getters["ConfigStore/isTeacherApp"];
    },
    isGuest() {
      return this.$store.getters["UserStore/isGuest"]();
    },
    lessons() {
      return this.$store.getters["LessonsStore/getLessons"];
    },
    user() {
      return this.$store.getters["UserStore/getUser"];
    },
    studentLabel() {
      const name = `${this.user.firstName || ""} ${
        this.user.lastName || ""
      }`.trim();
      const grade = this.user.grade ? `${this.user.grade}-й клас` : "";
      return [name, grade].filter(Boolean).join(" ");
    },
    notificationButtonLabel() {
      const count = this.unreadNotifications.length;
      return count
        ? `Непрочитані повідомлення: ${count}`
        : "Нових повідомлень немає";
    },
    sidebarToggleLabel() {
      return !this.drawer || this.miniVariant
        ? "Розгорнути меню предметів"
        : "Згорнути меню до іконок";
    },
    route() {
      return this.$route;
    },
    stateName() {
      return this.$store.getters["ConfigStore/getCurrentState"];
    },
    login() {
      const authorizedPages = this.$store.getters[
        "ConfigStore/getAuthorizedPages"
      ];
      return authorizedPages.includes(this.stateName);
    },
  },
  watch: {
    route(newRoute, oldRoute) {
      if (newRoute.name !== oldRoute.name) {
        this.drawer = newRoute.name !== statesEnum.SETTINGS;
      }
      return this.$store.dispatch("ConfigStore/updateRoute", {
        from: oldRoute,
        to: newRoute,
      });
    },
  },
  async created() {
    this.drawer = this.$route.name !== statesEnum.SETTINGS;
    this.urlParams = this.parseUrls();

    const initConfig = {
      appName: appName.STUDENT_APP,
      demoToken: this.urlParams.demoToken,
    };
    await this.$store.dispatch("ConfigStore/createConfigs", initConfig);
    const configView = this.$store.getters["ConfigStore/getConfigView"];
    this.$store.dispatch("ConfigStore/updateRoute", {
      from: {},
      to: this.route,
    });
    this.initServices(configView);
    this.subscribe();
    this.afterLogin();
    await this.loadUnreadNotifications();
    if (!this.isGuest) {
      this.notificationRefreshTimer = window.setInterval(
        () => this.loadUnreadNotifications(),
        60000
      );
    }
    if (this.user) {
      this.$store.dispatch("UserActivityStore/initStore");
    }
    this.inited = true;
    GoogleAnaliticService.trackUserAction(this.$store, "loadApp");
  },
  beforeDestroy() {
    if (this.notificationRefreshTimer) {
      window.clearInterval(this.notificationRefreshTimer);
    }
  },
  methods: {
    subscribe() {
      this.$store.subscribeAction({
        after: async (action, state) => {
          switch (action.type) {
            case "PopupStore/openPopup":
              this.openPopup(action.payload);
              break;
            case "ChatStore/markAsRead":
              await this.loadUnreadNotifications();
              break;
            default:
              break;
          }
        },
      });
    },
    openPopup(payload) {
      const { popupName } = payload;
      this.currentPopup = this.$store.getters["PopupStore/getPopup"](popupName);
      this.currentPopupContext = this.$store.getters[
        "PopupStore/getPopupContext"
      ](popupName);
    },
    popupEventHandler(payload) {
      const { type } = payload;
      switch (type) {
        case PopupEventEnum.CLOSE_POPUP:
          this.currentPopup = null;
          break;

        default:
          break;
      }
    },
    parseUrls() {
      const urlParams = new URLSearchParams(window.location.search);
      const rawDemoToken = urlParams.get("demoToken") || "";
      // 'http://18.216.218.57/school/#/login/?demoToken=3422f64fa0f206caee5af107650c5f92588ed3f0'
      let demoToken = "";
      if (rawDemoToken.length === 40) {
        demoToken = rawDemoToken;
      }
      return { demoToken };
    },
    initServices(configView) {
      RestService.init(configView);
      GoogleAnaliticService.installAnalitic(this.$store);
    },
    courseItemsEventHandler: function () {},
    toggleSidebarSize() {
      if (!this.drawer) {
        this.drawer = true;
        this.miniVariant = false;
        return;
      }
      this.miniVariant = !this.miniVariant;
    },

    logout() {
      if (this.isGuest) {
        const redirectUrl = this.$store.getters[
          "ConfigStore/getRedirectLandingUrl"
        ];
        this.$_logout();
        window.location.href = redirectUrl;
        return;
      }
      this.$_logout();
    },
    $_logout() {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("rawUser");
      this.$store.dispatch("ConfigStore/removeDemoToken");
      this.$store.dispatch("UserStore/logout");
      this.$router.push({ name: statesEnum.LOGIN });
    },
    toLibrary() {
      this.$router.push({ name: statesEnum.LIBRARY });
    },
    toCalendar() {
      this.$router.push({ name: statesEnum.CALENDAR });
    },
    toMain() {
      const homeRoute = this.$store.getters["ConfigStore/getRouteAfterLogin"];
      if (this.$route.name !== homeRoute) {
        this.$router.push({ name: homeRoute });
      }
    },
    toSettings() {
      if (this.$route.name !== statesEnum.SETTINGS) {
        this.$router.push({ name: statesEnum.SETTINGS });
      }
    },
    async loadUnreadNotifications() {
      if (this.isGuest || !this.$store.getters["UserStore/getToken"]) {
        this.unreadNotifications = [];
        return;
      }
      try {
        const response = await MessageService.getUnreadMessages();
        this.unreadNotifications = Array.isArray(response.data)
          ? response.data
          : [];
      } catch (error) {}
    },
    notificationPreview(notification) {
      return String(notification.text || "Нове повідомлення")
        .replace(/\s+/g, " ")
        .trim();
    },
    formatNotificationDate(value) {
      if (!value) return "";
      return new Intl.DateTimeFormat("uk-UA", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value));
    },
    async openNotification(notification) {
      this.notificationMenuOpen = false;
      try {
        await this.$router.push({
          name: statesEnum.LESSON,
          params: { lessonId: notification.lesson_id },
          query: { courseId: notification.course_id },
        });
      } catch (error) {
        if (!error || error.name !== "NavigationDuplicated") throw error;
      }
    },
  },
};
</script>
<style>
@import "./style/myStyle.css";
</style>
<style lang="less">
.student-navigation-drawer {
  border-right: 1px solid var(--theme-border-decorative) !important;
  box-shadow: 8px 0 24px rgba(31, 41, 55, 0.05);
}

.student-navigation-drawer > .v-navigation-drawer__content {
  scrollbar-color: var(--theme-accent-decorative) transparent;
  scrollbar-width: thin;
}

.student-navigation-drawer > .v-navigation-drawer__content::-webkit-scrollbar {
  width: 6px;
}

.student-navigation-drawer
  > .v-navigation-drawer__content::-webkit-scrollbar-track {
  background: transparent;
}

.student-navigation-drawer
  > .v-navigation-drawer__content::-webkit-scrollbar-thumb {
  background: var(--theme-accent-decorative);
  border-radius: 999px;
}

.student-navigation-drawer
  > .v-navigation-drawer__content::-webkit-scrollbar-thumb:hover {
  background: #6c9f8b;
}

.student-navigation-drawer
  > .v-navigation-drawer__content::-webkit-scrollbar-button {
  display: none;
  height: 0;
  width: 0;
}

.student-drawer-toggle {
  align-items: center;
  background: var(--surface-primary);
  border: 1px solid var(--theme-accent-decorative);
  border-radius: 0 10px 10px 0;
  box-shadow: 4px 2px 14px rgba(31, 41, 55, 0.12);
  cursor: pointer;
  display: flex;
  height: 48px;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  transition: left 0.2s ease, background 0.15s, box-shadow 0.15s;
  width: 30px;
  z-index: 7;
}

.student-drawer-toggle--open {
  border-radius: 10px 0 0 10px;
  left: 270px;
}

.student-drawer-toggle--compact {
  left: 42px;
}

.student-drawer-toggle:hover {
  background: var(--theme-surface-decorative);
  box-shadow: 4px 2px 16px rgba(31, 41, 55, 0.18);
}

.student-drawer-toggle:focus-visible {
  outline: 3px solid var(--theme-accent-decorative);
  outline-offset: 2px;
}
.theme-shell
  .student-drawer-toggle
  .theme-navigation-accent.v-icon {
  color: var(--theme-header-background) !important;
}

.student-navigation-list {
  background: var(--theme-sidebar-background) !important;
  min-height: 100%;
  padding: 0 !important;
}

.student-toolbar {
  align-items: center;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  min-width: 0;
  width: 100%;
}

.student-toolbar__side,
.student-toolbar__brand {
  align-items: center;
  display: flex;
  min-width: 0;
}

.student-toolbar__side--left {
  justify-content: flex-start;
  padding-right: 32px;
}

.student-toolbar__side--right {
  gap: 4px;
  justify-content: flex-end;
  padding-left: 32px;
}

.student-toolbar__brand {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  justify-content: center;
  padding: 0;
  position: relative;
}

.student-toolbar__brand:focus-visible {
  border-radius: 8px;
  outline: 2px solid var(--theme-header-text);
  outline-offset: 4px;
}

.student-toolbar__brand--doodle .student-toolbar__brand-name {
  margin-left: 48px;
}

.student-toolbar__logo-wrap {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  height: 50px;
  justify-content: center;
  margin-right: 24px;
  position: relative;
  width: 50px;
}

.student-toolbar__logo {
  background-color: var(--surface-primary);
  background-image: url("./style/img/logoSmall.svg");
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 50%;
  flex: 0 0 auto;
  height: 50px;
  position: relative;
  z-index: 2;
  width: 50px;
}

.student-toolbar__brand-name,
.student-toolbar__user {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-toolbar__user {
  font-size: 16px;
  font-weight: 600;
  margin-right: 8px;
  min-width: 0;
}

.student-toolbar__logout,
.student-toolbar__notification {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 9px;
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  height: 40px;
  justify-content: center;
  padding: 0;
  width: 40px;
}

.student-toolbar__notification {
  position: relative;
}

.student-toolbar__notification:hover,
.student-toolbar__logout:hover {
  background: rgba(255, 255, 255, 0.12);
}

.student-toolbar__notification:focus-visible,
.student-toolbar__logout:focus-visible {
  outline: 2px solid var(--theme-header-text);
  outline-offset: 2px;
}

.student-toolbar__notification-dot {
  background: var(--semantic-error);
  border: 2px solid var(--theme-header-background);
  border-radius: 50%;
  height: 10px;
  position: absolute;
  right: 5px;
  top: 5px;
  width: 10px;
}

.student-notifications-menu {
  border: 1px solid var(--theme-border-decorative);
  border-radius: 13px !important;
  box-shadow: 0 16px 38px rgba(23, 32, 51, 0.2) !important;
  margin-top: 8px;
  overflow: hidden;
  width: min(380px, calc(100vw - 24px));
}

.student-notifications {
  background: var(--theme-app-background);
  color: var(--text-primary);
}

.student-notifications__header {
  align-items: center;
  border-bottom: 1px solid var(--theme-border-decorative);
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
}

.student-notifications__header strong,
.student-notifications__header span {
  display: block;
}

.student-notifications__header strong {
  font-size: 15px;
}

.student-notifications__header span {
  color: var(--text-secondary);
  font-size: 11px;
  margin-top: 2px;
}

.student-notifications__header .v-icon {
  color: var(--theme-header-background) !important;
}

.student-notifications__list {
  max-height: 234px;
  overflow-y: auto;
}

.student-notifications__list > button {
  align-items: center;
  background: var(--theme-app-background);
  border: 0;
  border-bottom: 1px solid var(--theme-border-decorative);
  color: inherit;
  cursor: pointer;
  display: flex;
  gap: 10px;
  min-height: 78px;
  padding: 11px 12px;
  text-align: left;
  width: 100%;
}

.student-notifications__list > button:last-child {
  border-bottom: 0;
}

.student-notifications__list > button:hover,
.student-notifications__list > button:focus-visible {
  background: var(--theme-surface-decorative);
  outline: none;
}

.student-notifications__unread {
  background: var(--semantic-error);
  border-radius: 50%;
  flex: 0 0 auto;
  height: 7px;
  width: 7px;
}

.student-notifications__content {
  display: block;
  flex: 1;
  min-width: 0;
}

.student-notifications__content strong,
.student-notifications__content > span,
.student-notifications__content small {
  display: block;
}

.student-notifications__content strong {
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-notifications__content > span {
  color: var(--text-secondary);
  display: -webkit-box;
  font-size: 12px;
  line-height: 1.35;
  margin-top: 3px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.student-notifications__content small {
  color: var(--text-secondary);
  font-size: 10px;
  margin-top: 4px;
}

.student-notifications__list .v-icon {
  color: var(--theme-header-background) !important;
}

.student-notifications__empty {
  align-items: center;
  color: var(--text-secondary);
  display: flex;
  flex-direction: column;
  font-size: 13px;
  gap: 7px;
  padding: 26px 16px;
}

.student-notifications__empty .v-icon {
  color: var(--theme-header-background) !important;
}

@media (max-width: 1263px) {
  .student-toolbar__brand-name {
    display: none;
  }

  .student-toolbar__logo {
    margin: 0;
  }

  .student-toolbar__logo-wrap {
    margin-right: 0;
  }
}

@media (max-width: 600px) {
  .student-toolbar {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .student-toolbar__brand {
    display: none;
  }

  .student-toolbar__side--left,
  .student-toolbar__side--right {
    padding: 0;
  }

  .student-toolbar__user {
    font-size: 14px;
  }

  .student-toolbar__user {
    margin-right: 2px;
  }
}

.mobile-paddings {
  padding-top: calc(env(safe-area-inset-top)) !important;
  padding-bottom: calc(env(safe-area-inset-bottom)) !important;
  padding-left: calc(env(safe-area-inset-left)) !important;
  padding-right: calc(env(safe-area-inset-right)) !important;
}
</style>
